import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { MdAdd, MdRemove, MdShoppingCart } from 'react-icons/md';
import { FaPepperHot } from 'react-icons/fa';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { fetchMenuDataRequest, type MenuItem } from '../redux/slices/menuSlice';
import { addToCart, openCart, updateQuantity } from '../redux/slices/cartSlice';
import { PageBackButton, VegDotIcon, DietaryOverlayIcon } from '../components';
import { 
  filterEnabledModifiers, 
  handleModifierOptionSelection,
  isModifierOptionSelected,
  isModifierRequired
} from '../utils/modifierUtils';
import { getPrimaryDietaryType } from '../utils/dietaryUtils';
import { calculateTotalPrice, parsePrice } from '../utils/priceUtils';
import { 
  shouldShowSpiceLevel, 
  SPICE_LEVELS, 
  DEFAULT_SPICE_LEVEL 
} from '../utils/spiceUtils';
import { validateAllSelections, type ValidationErrors } from '../utils/validationUtils';
import { createCartItem, generateCartItemId } from '../utils/cartUtils';
import { 
  ErrorState, 
  RecommendedProducts, 
  NutritionalInfo, 
  IngredientsSection, 
  AllergensSection,
  type SelectedModifier
} from '../components/product-details';
import { AddToCartModal } from '../components/AddToCartModal';
import { fetchTpnConfigRequest } from '../redux/slices/tpnConfigSlice';
import { useTpnCart } from '../hooks/useTpnCart';

// Loading State Component
const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-20">
    <div className="text-center">
      <div className="animate-spin h-12 w-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading Menu Item...</h2>
      <p className="text-gray-600">Please wait while we load the item details.</p>
    </div>
  </div>
);


// Product Header Component
const ProductHeader: React.FC<{ 
  item: MenuItem; 
  formatPrice: (price: string | number) => string;
}> = ({ item, formatPrice }) => {
  const dietaryType = getPrimaryDietaryType(item);
  const showVegDot = dietaryType === 'vegetarian' || dietaryType === 'vegan';
  
  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        {/* FSSAI Dietary Indicator - Only show for veg/vegan items */}
        {showVegDot && <VegDotIcon item={item} size="md" />}
        <h1 className="text-2xl sm:text-3xl font-bold">{item.name}</h1>
      </div>
      
      {item.description && (
        <div className="text-gray-700 mb-3 text-sm sm:text-base leading-relaxed">
          <p className="whitespace-pre-wrap">{item.description}</p>
        </div>
      )}
      
      <div className="text-lg sm:text-xl font-bold text-red-500 mb-4">
        {formatPrice(item.price)}
      </div>
    </section>
  );
};

// Spice Level Selector Component
const SpiceLevelSelector: React.FC<{
  spiceLevel: number;
  setSpiceLevel: (level: number) => void;
  validationErrors: ValidationErrors;
}> = ({ spiceLevel, setSpiceLevel, validationErrors }) => (
  <section>
    <h3 className="font-semibold mb-2 text-base">
      Spice Level
      <span className="text-red-500 text-sm ml-1">*</span>
    </h3>
    <div className="grid grid-cols-3 gap-2">
      {SPICE_LEVELS.map((level) => (
        <button
          key={level.id}
          onClick={() => setSpiceLevel(level.id)}
          aria-label={`Select ${level.pepperCount} pepper spice level`}
          className={`p-2 sm:p-3 rounded-lg border text-left transition-colors flex items-center ${
            spiceLevel === level.id
              ? 'border-red-500 bg-red-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="mr-2">
            <div className={`w-4 h-4 border rounded-full ${spiceLevel === level.id ? 'border-red-500' : 'border-gray-400'} flex items-center justify-center`}>
              {spiceLevel === level.id && <div className="w-2 h-2 rounded-full bg-red-500"></div>}
            </div>
          </div>
          <div className="font-medium flex items-center justify-center text-sm sm:text-base">
            <div className="flex">
              {Array.from({ length: level.pepperCount }, (_, i) => (
                <FaPepperHot 
                  key={i}
                  className={`h-4 w-4 sm:h-5 sm:w-5 ${i > 0 ? 'ml-1' : ''} text-red-600`} 
                />
              ))}
            </div>
          </div>
        </button>
      ))}
    </div>
    {validationErrors['Spice Level'] && (
      <div className="text-red-500 text-xs mt-1.5 font-medium" role="alert">
        {validationErrors['Spice Level']}
      </div>
    )}
  </section>
);

// Modifiers Selector Component
const ModifiersSelector: React.FC<{
  modifiersList: any[];
  selectedModifiers: SelectedModifier[];
  onModifierSelect: (modifierName: string, option: any) => void;
  validationErrors: ValidationErrors;
  formatPrice: (price: string | number) => string;
}> = ({ modifiersList, selectedModifiers, onModifierSelect, validationErrors, formatPrice }) => {
  
  const isModifierOptionSelectedLocal = useCallback((modifierName: string, optionName: string): boolean => {
    return isModifierOptionSelected(modifierName, optionName, selectedModifiers);
  }, [selectedModifiers]);

  return (
    <>
      {modifiersList.map((modifier, index) => (
        <section key={index}>
          <h3 className="font-semibold mb-2 text-base">
            {modifier.name}
            {modifier.is_forced?.toLowerCase() === 'yes' && (
              <span className="text-red-500 text-sm ml-1">*</span>
            )}
          </h3>
          <div className="space-y-2">
            {modifier.options.map((option: any, optIndex: number) => (
              <button
                key={optIndex}
                onClick={() => onModifierSelect(modifier.name, option)}
                aria-label={`${isModifierOptionSelectedLocal(modifier.name, option.name) ? 'Remove' : 'Add'} ${option.name}`}
                className={`w-full p-2.5 rounded-lg border text-left transition-colors flex items-center justify-between ${
                  isModifierOptionSelectedLocal(modifier.name, option.name)
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <div className="mr-2.5">
                    {modifier.is_multi_select?.toLowerCase() === 'yes' ? (
                      <div className={`w-4 h-4 border rounded ${isModifierOptionSelectedLocal(modifier.name, option.name) ? 'bg-red-500 border-red-500' : 'border-gray-400'} flex items-center justify-center`}>
                        {isModifierOptionSelectedLocal(modifier.name, option.name) && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    ) : (
                      <div className={`w-4 h-4 border rounded-full ${isModifierOptionSelectedLocal(modifier.name, option.name) ? 'border-red-500' : 'border-gray-400'} flex items-center justify-center`}>
                        {isModifierOptionSelectedLocal(modifier.name, option.name) && <div className="w-2 h-2 rounded-full bg-red-500"></div>}
                      </div>
                    )}
                  </div>
                  <span className="font-medium text-sm">{option.name}</span>
                </div>
                <span className="text-xs text-gray-600 font-medium">
                  +{formatPrice(option.price)}
                </span>
              </button>
            ))}
          </div>
          {validationErrors[modifier.name] && (
            <div className="text-red-500 text-xs mt-1.5 font-medium" role="alert">
              {validationErrors[modifier.name]}
            </div>
          )}
        </section>
      ))}
    </>
  );
};

// Cart Controls Component
const CartControls: React.FC<{
  currentQuantity: number;
  totalPrice: number;
  formatPrice: (price: string | number) => string;
  handleQuantityChange: (change: number) => void;
  handleAddToCart: () => void;
  isAddingToCart: boolean;
  isOutOfStock?: boolean;
}> = ({ currentQuantity, totalPrice, formatPrice, handleQuantityChange, handleAddToCart, isAddingToCart, isOutOfStock = false }) => (
  <section className="pt-4 border-t border-gray-200">
    <div className="grid grid-cols-1 gap-4">
      {/* Left Side - Total Price */}
      {/* <div className="flex flex-col justify-center">
        <p className="text-sm text-gray-500 mb-1">Total</p>
        <span className="text-2xl font-bold text-gray-900">
          {formatPrice(totalPrice)}
        </span>
      </div> */}

      {/* Right Side - Smart Cart Controls */}
      <div className="flex flex-col justify-center">
        {isOutOfStock ? (
          // Out of Stock Message
          <div className="font-semibold py-3 px-4 rounded-xl bg-gray-400 text-gray-600 cursor-not-allowed flex items-center justify-center">
            <MdShoppingCart className="w-4 h-4 mr-2" />
            Out of Stock
          </div>
        ) : currentQuantity > 0 ? (
          // Quantity Controls - Separate Design
          <div className="flex items-center bg-gray-100 rounded-xl shadow-sm max-w-[140px] ml-auto">
            <button
              onClick={() => handleQuantityChange(-1)}
              disabled={currentQuantity <= 0}
              aria-label="Decrease quantity"
              className={`w-10 h-10 rounded-l-xl transition-all duration-200 flex items-center justify-center ${
                currentQuantity <= 0 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'hover:bg-gray-200 text-gray-600 hover:scale-105 active:scale-95'
              }`}
            >
              <MdRemove className="w-4 h-4" />
            </button>
            <div className="flex-1 py-2.5 font-bold text-gray-900 text-center bg-white mx-1 rounded-lg shadow-sm min-w-[3rem]">
              {currentQuantity}
            </div>
            <button
              onClick={() => handleQuantityChange(1)}
              disabled={currentQuantity >= 99}
              aria-label="Increase quantity"
              className={`w-10 h-10 rounded-r-xl transition-all duration-200 flex items-center justify-center ${
                currentQuantity >= 99 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'hover:bg-gray-200 text-gray-600 hover:scale-105 active:scale-95'
              }`}
            >
              <MdAdd className="w-4 h-4" />
            </button>
          </div>
        ) : (
          // Add to Cart Button - Separate Design
          <button
            onClick={handleAddToCart}
            disabled={isAddingToCart}
            aria-label="Add item to cart"
            className={`font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform flex items-center justify-center ${
              isAddingToCart
                ? 'bg-gray-400 cursor-not-allowed scale-95'
                : 'bg-red-500 hover:bg-red-600 text-white hover:scale-105 active:scale-95'
            }`}
          >
            {isAddingToCart ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Adding...
              </div>
            ) : (
              <>
                <MdShoppingCart className="w-4 h-4 mr-2" />
                Add to Order
              </>
            )}
          </button>
        )}
      </div>
    </div>
  </section>
);

// Left Panel Component
const LeftPanel: React.FC<{
  item: MenuItem;
  formatPrice: (price: string | number) => string;
  totalPrice: number;
  currentQuantity: number;
  handleQuantityChange: (change: number) => void;
  handleAddToCart: () => void;
  isAddingToCart: boolean;
  showCartControls: boolean;
  handleImageError: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}> = ({ 
  item, 
  formatPrice, 
  totalPrice, 
  currentQuantity, 
  handleQuantityChange, 
  handleAddToCart, 
  isAddingToCart,
  showCartControls,
  handleImageError
}) => {
  // Check if item is out of stock
  const isOutOfStock = !item.inventory_status;

  return (
    <div className="space-y-6">
      {/* Product Image - Only show if image exists */}
      {item.image && (
        <div className="relative rounded-lg overflow-hidden shadow-lg max-w-sm">
          {/* Dietary Icons Overlay */}
          <div className="absolute top-2 right-2 z-10">
            <DietaryOverlayIcon item={item} size="lg" />
          </div>
          
          <div className="aspect-[4/3]">
            <img 
              src={item.image} 
              alt={item.name} 
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
          </div>
        </div>
      )}

      <ProductHeader item={item} formatPrice={formatPrice} />

      {showCartControls && (
        <CartControls 
          currentQuantity={currentQuantity}
          totalPrice={totalPrice}
          formatPrice={formatPrice}
          handleQuantityChange={handleQuantityChange}
          handleAddToCart={handleAddToCart}
          isAddingToCart={isAddingToCart}
          isOutOfStock={isOutOfStock}
        />
      )}
    </div>
  );
};

// Right Panel Component
const RightPanel: React.FC<{
  item: MenuItem;
  menuData: MenuItem[] | null;
  onRecommendedClick: (item: MenuItem) => void;
}> = ({ item, menuData, onRecommendedClick }) => (
  <aside className="space-y-6">
    {/* Recommended Products - Moved to TOP of right side */}
    <RecommendedProducts 
      currentProductId={item.pk_id.toString()} 
      allItems={menuData || []} 
      product={item}
      showPrice={true}
      onItemClick={onRecommendedClick}
    />

    {/* Additional Information Sections */}
    <div className="space-y-4">
      <NutritionalInfo product={item} />
      <IngredientsSection product={item} />
      <AllergensSection product={item} />
    </div>
  </aside>
);

// Main Component
const MenuItemDetailPage: React.FC = () => {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  
  const { loading, error, data: menuData } = useAppSelector(state => state.menu);
  const { selectedRestaurant } = useAppSelector(state => state.restaurant);
  const { items: cartItems } = useAppSelector(state => state.cart);
  const shouldShowCart = useTpnCart(true);

  const item = useMemo(() => 
    menuData?.find((menuItem: MenuItem) => menuItem.pk_id?.toString() === itemId),
    [menuData, itemId]
  );
  
  const [currentItem, setCurrentItem] = useState<MenuItem | null>(item || null);
  
  // Modal state
  const [modalItem, setModalItem] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch menu data if not loaded
  useEffect(() => {
    if (!menuData && selectedRestaurant?.restaurant_id) {
      dispatch(fetchMenuDataRequest());
    }
     dispatch(fetchTpnConfigRequest())
  }, [dispatch, menuData, selectedRestaurant?.restaurant_id]);

  // Update current item when menu data loads or itemId changes
  useEffect(() => {
    if (menuData && itemId) {
      const foundItem = menuData.find((menuItem: MenuItem) => menuItem.pk_id.toString() === itemId);
      setCurrentItem(foundItem || null);
    }
  }, [menuData, itemId]);

  // Scroll to top when component mounts or itemId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [itemId]);

  // Memoized computations
  const modifiersList = useMemo(() => 
    filterEnabledModifiers(currentItem?.modifiers_list || []), 
    [currentItem?.modifiers_list]
  );

  const formatter = useMemo(
    () => new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    []
  );

  const formatPrice = useCallback((price: string | number) => {
    const numPrice = typeof price === "string" ? parsePrice(price) : price;
    return formatter.format(numPrice || 0);
  }, [formatter]);

  const totalPrice = useMemo(() => {
    if (!currentItem) return 0;
    return parsePrice(currentItem.price);
  }, [currentItem]);

  // Event handlers
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.src = `https://via.placeholder.com/600x400/f8f9fa/6c757d?text=${encodeURIComponent(currentItem?.name || 'Item')}`;
  }, [currentItem?.name]);

  const handleQuantityChange = useCallback((change: number) => {
    // Quantity changes are now handled per cart item, no need for state
    console.log('Quantity change:', change);
  }, []);

  // Check if item needs customization before adding to cart
  const needsCustomization = useCallback((item: MenuItem): boolean => {
    const hasSpiceLevel = shouldShowSpiceLevel(item);
    const enabledModifiers = filterEnabledModifiers(item.modifiers_list || []);
    const hasRequiredModifiers = enabledModifiers.some(modifier => isModifierRequired(modifier));
    return hasSpiceLevel || hasRequiredModifiers;
  }, []);

  // Modal handlers
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setModalItem(null);
  }, []);

  const handleModalAddToCart = useCallback((
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: SelectedModifier[]
  ) => {
    const cartItem = createCartItem(item, spiceLevel, modifiers, quantity);
    dispatch(addToCart(cartItem));
    setIsModalOpen(false);
    setModalItem(null);
    dispatch(openCart());
  }, [dispatch]);

  const handleAddToCart = useCallback(() => {
    if (!currentItem) return;
    
    if (needsCustomization(currentItem)) {
      // Open modal for items with modifiers/spice
      setModalItem(currentItem);
      setIsModalOpen(true);
    } else {
      // Add directly to cart for simple items
      const cartItem = {
        item: currentItem,
        selected: {
          spice: null,
          modifiers: [],
        },
        totalPrice: parsePrice(currentItem.price),
        cartItemId: `${currentItem.pk_id}-basic-${Date.now()}`,
        quantity: 1,
      };
      dispatch(addToCart(cartItem));
      dispatch(openCart());
    }
  }, [currentItem, needsCustomization, dispatch]);

  const handleRecommendedItemClick = useCallback((newItem: MenuItem) => {
    navigate(`/menu-item/${newItem.pk_id}`);
  }, [navigate]);

  // UI States
  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;
  if (!currentItem) return null;

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <PageBackButton text="Menu" onClick={() => navigate('/menu')} />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pb-8">
          <LeftPanel
            item={currentItem}
            formatPrice={formatPrice}
            totalPrice={totalPrice}
            currentQuantity={0}
            handleQuantityChange={shouldShowCart ? handleQuantityChange : () => {}}
            handleAddToCart={shouldShowCart ? handleAddToCart : () => {}}
            isAddingToCart={false}
            showCartControls={shouldShowCart}
            handleImageError={handleImageError}
          />
          <RightPanel 
            item={currentItem} 
            menuData={menuData}
            onRecommendedClick={handleRecommendedItemClick}
          />
        </div>
      </div>

      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={isModalOpen}
        item={modalItem}
        onClose={handleModalClose}
        onAddToCart={handleModalAddToCart}
      />
    </main>
  );
};

export default MenuItemDetailPage;
